import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, Coffee, Store, Crown, Tag } from 'lucide-react';
import { staggerContainer, fadeInUp } from '@/hooks/use-scroll-animation';
import { usePlatform } from '@/hooks/use-platform';
import { Button } from '@/components/ui/button';
import { PricingMobileCarousel } from './pricing-mobile-carousel';
import { WaitlistModal } from './waitlist-modal';

/* ── animation variants ─────────────────────────────── */
const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    }),
};

/* ── plan card configs ───────────────────────────────── */
const PLAN_CONFIG = {
    free: {
        icon: Coffee,
        checkColor: 'text-[#6F4E37]',
        cardClass:
            'bg-white/80 backdrop-blur-sm border border-[#C8A27A]/30 shadow-md hover:shadow-lg hover:border-[#C8A27A]/50',
        ctaVariant: 'outline' as const,
        order: 'order-2 md:order-1',
    },
    premium: {
        icon: Crown,
        checkColor: 'text-[#DAA520]',
        cardClass:
            'bg-white border-2 border-[#DAA520] shadow-[0_0_30px_rgba(218,165,32,0.15)] md:scale-105 z-10',
        ctaVariant: 'default' as const,
        order: 'order-1 md:order-2',
    },
    business: {
        icon: Store,
        checkColor: 'text-[#6F4E37]',
        cardClass:
            'bg-white/80 backdrop-blur-sm border border-[#6B5344]/25 shadow-md hover:shadow-lg hover:border-[#6B5344]/40',
        ctaVariant: 'outline' as const,
        order: 'order-3 md:order-3',
    },
} as const;

type PlanKey = keyof typeof PLAN_CONFIG;

/* ═══════════════════════════════════════════════════════
   PricingSection
   ═══════════════════════════════════════════════════════ */
export function PricingSection() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { platform } = usePlatform();
    const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);

    const plans: PlanKey[] = ['free', 'premium', 'business'];

    const handleCtaClick = (plan: PlanKey) => {
        if (plan === 'free') {
            // Coffeelover → redirect to app store
            if (platform === 'ios') {
                window.open('https://apps.apple.com', '_blank');
            } else {
                window.open('https://play.google.com', '_blank');
            }
        } else if (plan === 'premium') {
            // Premium → navigate to register with plan pre-selected
            navigate('/register?plan=premium');
        } else if (plan === 'business') {
            // Business → open waitlist modal
            setIsWaitlistOpen(true);
        }
    };

    return (
        <section
            id="pricing-section"
            className="relative py-20 md:py-28 overflow-hidden"
            style={{
                width: '100%',
                background: 'linear-gradient(180deg, #FDF8F3 0%, #F5EBE0 100%)',
            }}
        >
            <div className="container mx-auto px-4" style={{ width: '100%' }}>
                {/* ── Section Header ──────────────────────────── */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={staggerContainer}
                    className="text-center mb-14 md:mb-20 space-y-5"
                >
                    {/* Badge — Padronizado com Consumer/Business Sections */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6F4E37]/10 text-[#6F4E37] text-sm font-medium mx-auto"
                    >
                        <Tag size={14} />
                        <span className="uppercase tracking-wider text-xs font-bold">
                            {t('pricing.badge')}
                        </span>
                    </motion.div>

                    {/* Headline */}
                    <motion.h2
                        variants={fadeInUp}
                        className="text-3xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-[#2D1810] leading-tight"
                    >
                        {t('pricing.headline')}
                    </motion.h2>

                    {/* Freemium pill */}
                    <motion.p variants={fadeInUp}>
                        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#6F4E37]/10 text-[#6F4E37] text-base md:text-lg font-semibold">
                            {t('pricing.freemium')}
                        </span>
                    </motion.p>
                </motion.div>

                {/* ── Mobile Carousel ──────────────────────────── */}
                <PricingMobileCarousel />

                {/* ── Desktop Cards Grid ───────────────────────── */}
                <div className="hidden xl:grid xl:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
                    {plans.map((plan, index) => {
                        const config = PLAN_CONFIG[plan];
                        const isPremium = plan === 'premium';
                        const isBusiness = plan === 'business';
                        const Icon = config.icon;

                        return (
                            <motion.div
                                key={plan}
                                custom={index}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={cardVariants}
                                className={`relative ${config.order} ${isPremium ? 'md:-mt-6 md:mb-6' : ''}`}
                            >
                                {/* Card — badge is INSIDE so it follows hover transform */}
                                <div
                                    className={`relative h-full flex flex-col rounded-[32px] p-8 pt-10 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl overflow-visible ${config.cardClass} ${isPremium ? 'animate-[glow-pulse_3s_ease-in-out_infinite]' : ''}`}
                                >
                                    {/* Badge (Premium & Business) — inside card, follows hover */}
                                    {(isPremium || isBusiness) && (
                                        <div className="absolute -top-4 left-0 right-0 flex justify-center z-50">
                                            <span
                                                className={`text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest ${isPremium
                                                    ? 'bg-gradient-to-r from-[#DAA520] to-[#B87333] text-white shadow-lg shadow-amber-500/25'
                                                    : 'bg-[#EDE5DC] text-[#6B5344] border border-[#6B5344]/20'
                                                    }`}
                                            >
                                                {t(`pricing.plans.${plan}.badge`)}
                                            </span>
                                        </div>
                                    )}
                                    {/* Card Header */}
                                    <div className="mb-6">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isPremium
                                                ? 'bg-gradient-to-br from-[#DAA520] to-[#B87333] text-white'
                                                : 'bg-[#6F4E37]/10 text-[#6F4E37]'
                                                }`}>
                                                <Icon className="w-5 h-5" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-[#2D1810]">
                                                {t(`pricing.plans.${plan}.name`)}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-[#6B5344]">
                                            {t(`pricing.plans.${plan}.description`)}
                                        </p>
                                    </div>

                                    {/* Price Block */}
                                    <div className="mb-6">
                                        {isPremium ? (
                                            /* Premium: hero price + tiered */
                                            <div className="space-y-3">
                                                <div>
                                                    <span className="text-4xl md:text-5xl font-bold text-[#2D1810]">
                                                        {t('pricing.plans.premium.heroPrice')}
                                                    </span>
                                                    <p className="text-sm text-[#9C8979] font-medium mt-1">
                                                        {t('pricing.plans.premium.heroSubtext')}
                                                    </p>
                                                </div>
                                                {/* Tiered pricing — subtle */}
                                                <div className="bg-[#F5EDE4] rounded-xl p-3 space-y-1">
                                                    <p className="text-xs font-semibold text-[#6B5344] uppercase tracking-wide">
                                                        {t('pricing.plans.premium.tiers.label')}
                                                    </p>
                                                    {(
                                                        t('pricing.plans.premium.tiers.items', {
                                                            returnObjects: true,
                                                        }) as string[]
                                                    )?.map((tier, i) => (
                                                        <p key={i} className="text-xs text-[#9C8979]">
                                                            {tier}
                                                        </p>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            /* Free & Business: standard price */
                                            <div>
                                                <span className="text-4xl font-bold text-[#2D1810]">
                                                    {t(`pricing.plans.${plan}.price`)}
                                                </span>
                                                <span className="text-[#9C8979] ml-1">
                                                    {t(`pricing.plans.${plan}.period`)}
                                                </span>
                                                {isBusiness && (
                                                    <p className="text-sm text-[#6B5344] mt-1">
                                                        {t('pricing.plans.business.subtitle')}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Features List */}
                                    <ul className="space-y-3 text-sm flex-1 mb-8">
                                        {(
                                            t(`pricing.plans.${plan}.features`, {
                                                returnObjects: true,
                                            }) as string[]
                                        )?.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-2.5">
                                                <Check
                                                    className={`h-4 w-4 mt-0.5 shrink-0 ${config.checkColor}`}
                                                />
                                                <span className="text-[#6B5344]">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* CTA */}
                                    <Button
                                        className={`w-full rounded-full py-6 text-base font-semibold transition-all duration-300 ${isPremium
                                            ? 'bg-[#6F4E37] hover:bg-[#5D3D2E] text-white hover:scale-[1.02] hover:shadow-lg'
                                            : isBusiness
                                                ? 'border-[#6B5344]/30 text-[#6B5344] hover:bg-[#6B5344]/5'
                                                : 'border-[#C8A27A] text-[#6F4E37] hover:bg-[#6F4E37]/5'
                                            }`}
                                        variant={config.ctaVariant}
                                        onClick={() => handleCtaClick(plan)}
                                    >
                                        {t(`pricing.plans.${plan}.cta`)}
                                    </Button>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* ── Social Proof ────────────────────────────── */}
                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6, duration: 0.8 }}
                    className="text-center text-sm text-[#9C8979] mt-12 italic"
                >
                    {t('pricing.socialProof')}
                </motion.p>
            </div>

            {/* ── Waitlist Modal ──────────────────────────── */}
            <WaitlistModal open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen} />

            {/* ── Glow pulse keyframe (injected via style tag) ── */}
            <style>{`
                @keyframes glow-pulse {
                    0%, 100% { box-shadow: 0 0 20px rgba(218, 165, 32, 0.15); }
                    50% { box-shadow: 0 0 35px rgba(218, 165, 32, 0.25); }
                }
            `}</style>
        </section>
    );
}
